import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import {
  AuditService,
  AuditRow,
  AuditAction,
  AuditFeedParams,
} from '../../services/audit.service';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { AuditFeedStateService } from '../../services/audit-feed-state.service';

const PAGE_SIZE_DEFAULT = 25;

@Component({
  standalone: true,
  selector: 'app-internal-dashboard',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
  ],
  templateUrl: './internal-dashboard.component.html',
  styleUrl: './internal-dashboard.component.scss',
  providers: [DatePipe],
})
export class InternalDashboardComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns = ['timestamp', 'user', 'entityType', 'entityLabel', 'action', 'summary'];

  rows: AuditRow[] = [];
  totalCount = 0;
  page = 0;
  pageSize = PAGE_SIZE_DEFAULT;

  loading = false;
  error = false;

  // Filter model
  filterSearch = '';
  filterState = '';
  filterLocation = '';
  filterEntityType = '';
  filterAction = '';
  filterUser = '';
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;

  readonly entityTypes = ['user', 'project', 'site', 'proposal', 'document'];
  readonly actions: AuditAction[] = ['CREATED', 'UPDATED', 'DELETED'];
  readonly stateOptions = Array.from(
    this.commonUtility.stateMap,
    ([code, name]) => ({ code, name })
  );

  // FIX 4: carry the serialised text-filter value so distinctUntilChanged can
  // actually suppress re-emission of the same value (void subjects pass every emission).
  private filterChange$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private auditService: AuditService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private commonUtility: CommonUtilityServiceService,
    private auditFeedState: AuditFeedStateService,
  ) {}

  ngOnInit(): void {
    // Debounce filter changes so free-text fields don't fire on every keystroke.
    this.filterChange$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map(() => undefined),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.resetToFirstPage();
        this.syncUrl();
        this.loadFeed();
      });

    // Restore filters + pagination straight from the URL query params so a reload
    // (or a "back" from a drill-down) lands on the same view. Fields are assigned
    // directly here — NOT routed through onFilterChange — so the debounced
    // filterChange$ pipeline is not re-triggered and the feed loads exactly once.
    this.restoreStateFromUrl();
    this.loadFeed();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterChange(): void {
    // FIX 4: emit the combined text-filter key so distinctUntilChanged suppresses
    // repeated identical values (e.g. holding a key that produces the same string).
    const key = `${this.filterSearch.trim()}|${this.filterLocation.trim()}|${this.filterUser.trim()}`;
    this.filterChange$.next(key);
  }

  onDropdownChange(): void {
    // Dropdowns and date pickers apply immediately (no debounce needed).
    this.resetToFirstPage();
    this.syncUrl();
    this.loadFeed();
  }

  /**
   * Resets to page 0 and keeps the paginator UI in sync (FIX 2), WITHOUT emitting
   * the paginator's (page) event. firstPage() would emit when currently past page 0,
   * re-entering onPageChange() and firing a second syncUrl()/loadFeed(); assigning
   * pageIndex directly avoids that duplicate request while preserving the UI reset.
   */
  private resetToFirstPage(): void {
    this.page = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  /**
   * True when at least one filter holds a non-default value. Drives the
   * visibility of the "Clear filters" button — it only appears when there is
   * something to clear.
   */
  get hasActiveFilters(): boolean {
    return !!(
      this.filterSearch.trim() ||
      this.filterState ||
      this.filterLocation.trim() ||
      this.filterEntityType ||
      this.filterAction ||
      this.filterUser.trim() ||
      this.filterDateFrom ||
      this.filterDateTo
    );
  }

  /**
   * Resets every filter to its default, returns to page 0, clears the URL query
   * params (buildQueryParams omits defaults, so syncUrl leaves a clean URL) and
   * reloads the feed.
   */
  clearFilters(): void {
    this.filterSearch = '';
    this.filterState = '';
    this.filterLocation = '';
    this.filterEntityType = '';
    this.filterAction = '';
    this.filterUser = '';
    this.filterDateFrom = null;
    this.filterDateTo = null;
    // Clear a lingering export-error banner too — it's only otherwise reset on the
    // next export attempt, so it would survive a filter reset.
    this.exportError = false;
    this.resetToFirstPage();
    this.syncUrl();
    this.loadFeed();
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.syncUrl();
    this.loadFeed();
  }

  onRowClick(row: AuditRow): void {
    // Capture the dashboard URL + scroll position before drilling in so the
    // drill-down's "back" can restore this exact view. Scroll target is the
    // window: this page has no inner vertical overflow container (the dashboard
    // shell and app content wrapper both scroll at the document level), so
    // window.scrollY is the offset to preserve.
    this.auditFeedState.capture(this.router.url, window.scrollY);
    this.router.navigate(['/internal/audit', row.entityType, row.entityId], {
      queryParams: { commit: row.commitId },
    });
  }

  exportError = false;

  onExportCsv(): void {
    this.exportError = false;
    // Subscribe with an error handler so HTTP failures are surfaced, and tie the subscription to the
    // component lifecycle (takeUntil destroy$) like loadFeed — so an in-flight export can't flip
    // exportError on a destroyed component after navigation.
    this.auditService.exportCsv(this.buildParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          this.exportError = true;
        },
      });
  }

  chipClass(action: AuditAction): string {
    switch (action) {
      case 'CREATED': return 'chip-created';
      case 'UPDATED': return 'chip-updated';
      case 'DELETED': return 'chip-deleted';
    }
  }

  /**
   * Returns true when the user string looks like a service account rather than
   * a human. Heuristic: no space (single-token name) OR contains "API".
   */
  isServiceAccount(user: string): boolean {
    return !user.includes(' ') || /api/i.test(user);
  }

  /**
   * Returns 1–2 initials for the avatar bubble.
   * For a service account like "CREtelligent API" → "CA".
   * For a human like "Rainier Dela Cruz" → "RD".
   */
  initials(user: string): string {
    const parts = user.trim().split(/\s+/);
    if (parts.length === 1) {
      // Single token — take first two uppercase chars (e.g. "CREtelligentAPI" → "CR")
      return parts[0].replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /**
   * Splits a record label into a leading id/code portion and the rest.
   * Returns null if no clean id prefix is detectable.
   * Examples:
   *   "25F30-50361-949 EnviroPreScreen Report.pdf"  → { prefix: "", id: "25F30-50361-949", suffix: " EnviroPreScreen Report.pdf" }
   *   "Proposal 3a41ea50 (ACTIVE)"                  → { prefix: "Proposal ", id: "3a41ea50", suffix: " (ACTIVE)" }
   *   "Rainier Dela Cruz"                            → null
   */
  splitRecordLabel(label: string): { prefix: string; id: string; suffix: string } | null {
    if (!label) return null;

    // Pattern A: leading code before a space — hex-like or dash-separated code as first token
    // e.g. "25F30-50361-949 EnviroPreScreen Report.pdf" or "2604-00467 Signed Award.pdf"
    const leadingCode = label.match(/^([A-Z0-9]{4,}[-A-Z0-9]*)\s(.+)$/i);
    if (leadingCode) {
      return { prefix: '', id: leadingCode[1], suffix: ' ' + leadingCode[2] };
    }

    // Pattern B: "Word hex-id (STATUS)" — e.g. "Proposal 3a41ea50 (ACTIVE)"
    const wordHex = label.match(/^([A-Za-z]+\s+)([0-9a-f]{6,})(\s.*)$/i);
    if (wordHex) {
      return { prefix: wordHex[1], id: wordHex[2], suffix: wordHex[3] };
    }

    return null;
  }

  /**
   * Serialises the current filter + pagination state into a clean URL query-param
   * object, omitting empty strings, null dates, and default values (page 0,
   * default page size) so the URL stays tidy. Dates use the same yyyy-MM-dd,
   * local-calendar form as buildParams(), so restoreStateFromUrl() round-trips
   * them without a UTC shift.
   */
  private buildQueryParams(): Params {
    // Reuse buildParams() for the filter fields (identical key names + date format)
    // so the URL and the feed request can't drift apart, then add only the extra
    // pagination params the URL needs. page/pageSize are omitted at their defaults
    // to keep the URL clean.
    const qp: Params = { ...this.buildParams() };
    if (this.page !== 0)                      qp['page']     = this.page;
    if (this.pageSize !== PAGE_SIZE_DEFAULT)  qp['pageSize'] = this.pageSize;
    return qp;
  }

  /**
   * Writes the current filter + pagination state to the dashboard URL. replaceUrl
   * keeps per-keystroke/filter changes out of the browser history so Back doesn't
   * step through every intermediate filter state.
   */
  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildQueryParams(),
      replaceUrl: true,
    });
  }

  /**
   * Restores filters + pagination from the URL query params. Assigns the ngModel
   * fields directly (bypassing onFilterChange) so the debounced filterChange$
   * pipeline is not re-triggered on init.
   */
  private restoreStateFromUrl(): void {
    const qpm = this.route.snapshot.queryParamMap;
    this.filterSearch     = qpm.get('search')     ?? '';
    this.filterState      = qpm.get('state')      ?? '';
    this.filterLocation   = qpm.get('location')   ?? '';
    this.filterEntityType = qpm.get('entityType') ?? '';
    this.filterAction     = qpm.get('action')     ?? '';
    this.filterUser       = qpm.get('user')       ?? '';

    const dateFrom = qpm.get('dateFrom');
    this.filterDateFrom = dateFrom ? this.parseIsoDate(dateFrom) : null;
    const dateTo = qpm.get('dateTo');
    this.filterDateTo = dateTo ? this.parseIsoDate(dateTo) : null;

    const page = qpm.get('page');
    const parsedPage = page ? parseInt(page, 10) : NaN;
    this.page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 0;

    const pageSize = qpm.get('pageSize');
    const parsedSize = pageSize ? parseInt(pageSize, 10) : NaN;
    this.pageSize = Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : PAGE_SIZE_DEFAULT;
  }

  /**
   * Parses a yyyy-MM-dd string into a local-midnight Date (matching how
   * buildQueryParams serialises dates). Using explicit y/m/d args avoids the
   * UTC-midnight parsing of new Date('yyyy-MM-dd'), which would shift the day
   * backwards for locales behind UTC. Returns null for a malformed value or an
   * out-of-range calendar date (e.g. 2026-13-45), so a tampered/garbled query
   * param restores as null rather than as a silently-normalised wrong date.
   */
  private parseIsoDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = Number(m[3]);
    const date = new Date(year, month, day);
    // The Date constructor rolls over out-of-range parts (month 12 -> next Jan,
    // day 45 -> next month); reject anything it didn't preserve exactly.
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }
    return date;
  }

  private buildParams(): AuditFeedParams {
    const params: AuditFeedParams = {};
    if (this.filterSearch.trim())     params.search     = this.filterSearch.trim();
    if (this.filterState)             params.state      = this.filterState;
    if (this.filterLocation.trim())   params.location   = this.filterLocation.trim();
    if (this.filterEntityType)        params.entityType = this.filterEntityType;
    if (this.filterAction)            params.action     = this.filterAction;
    if (this.filterUser.trim())       params.user       = this.filterUser.trim();
    // FIX 1: send plain calendar dates (yyyy-MM-dd) not full UTC timestamps.
    // Using DatePipe with local time avoids the UTC shift that toISOString() introduces
    // when the user's locale is behind UTC (e.g. a US date can roll back a day).
    if (this.filterDateFrom)
      params.dateFrom = this.datePipe.transform(this.filterDateFrom, 'yyyy-MM-dd') ?? undefined;
    if (this.filterDateTo)
      params.dateTo   = this.datePipe.transform(this.filterDateTo,   'yyyy-MM-dd') ?? undefined;
    return params;
  }

  private loadFeed(): void {
    this.loading = true;
    this.error = false;

    const params: AuditFeedParams = {
      ...this.buildParams(),
      page: this.page,
      size: this.pageSize,
    };

    this.auditService.getFeed(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.rows = data.rows;
        this.totalCount = data.totalCount;
        this.loading = false;
        // Restore the scroll position captured at row-click time, if any. One-shot:
        // consumeScrollOffset() clears after reading (even when we don't apply it) so a
        // stale offset can't leak into a later load. We only APPLY it when the current
        // dashboard URL matches the URL captured alongside the offset — i.e. the user
        // genuinely returned to the same filtered/paged view via "Back". Reaching the
        // dashboard another way (e.g. the header/nav "Audit Log" link → a fresh,
        // unfiltered view) has a different URL, so we discard the offset instead of
        // jumping to a position that belongs to different content.
        // setTimeout(0) defers until after the *ngIf-gated table has rendered; the
        // target is the window (document-level scroll — see onRowClick).
        const offset = this.auditFeedState.consumeScrollOffset();
        if (offset !== null && this.auditFeedState.getReturnUrl() === this.router.url) {
          setTimeout(() => window.scrollTo(0, offset));
        }
      },
      error: () => {
        this.rows = [];
        this.totalCount = 0;
        this.loading = false;
        this.error = true;
      },
    });
  }
}
